import React, { FC, useCallback, useState } from 'react';
import { Button, makeStyles, Typography } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { ReactComponent as AddIcon } from 'img/icons/add.svg';
import { IPageMeta } from 'common/interfaces/pagination';
import { IBodyCell, IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { IFilterSettings } from 'common/interfaces/filter';
import { IPolicyFormData, IPolicyListItem } from '../../interfaces/permissions';
import { TableOrderByParams } from 'common/constants/table';
import { ActionsMenu, ChangeStatusModal, Table } from 'common/components';
import { EditPolicyModal } from '..';
import ViewPolicyModal from '../ViewPolicyModal/ViewPolicyModal';
import { filterPermissionSet } from 'modules/permissions/utils/permission';
import { getAppPermissionLevelByGroup, getAppPermissionLevelLabel } from 'utils/permission.utils';
import tableHeaders from 'common/messages/tableHeaders';
import commonMessages from 'common/messages/messages';
import { PermissionsGroupsLabels } from 'modules/permissions/constants';
import { HEADER_ACTIONS_OPTION } from 'constants/table.constants';
import usePermissionLevelContext from 'common/hooks/context/usePermissionLevelContext';
import ActionItemEdit from 'components/ActionItems/ActionItemEdit';
import ActionItemChangeStatus from 'components/ActionItems/ActionItemChangeStatus';

const useStyles = makeStyles({
  icon: {
    width: 16,
    height: 16,
  },
});

interface IPoliciesTable {
  isPoliciesListLoading: boolean;
  isEditPolicyLoading: boolean;
  isChangePolicyStatusLoading: boolean;
  policiesList: IPolicyListItem[];
  policiesListMeta: IPageMeta;
  tableParams: ITableParams;
  tableFilterSettings: IFilterSettings[];
  handleChangeTableProps: (tableProps: ITableParams) => void;
  editItem: (id: string, data: IPolicyFormData, onClose: () => void) => void;
  changeStatusItem: (id: string, active: boolean, onClose: () => void) => void;
  createItem: (data: IPolicyFormData, onClose: () => void) => void;
}

const PoliciesTable: FC<IPoliciesTable> = ({
  isPoliciesListLoading,
  isEditPolicyLoading,
  isChangePolicyStatusLoading,
  policiesList,
  policiesListMeta,
  tableParams,
  tableFilterSettings,
  handleChangeTableProps,
  editItem,
  changeStatusItem,
  createItem,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  let btnCreate: JSX.Element | null = null;

  const {
    LEVEL_POLICY_CREATE,
    LEVEL_POLICY_EDIT,
    LEVEL_POLICY_CHANGE_STATUS,
  } = usePermissionLevelContext();

  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showViewDialog, setShowViewDialog] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<IPolicyListItem | null>(null);

  const headerOptions: IHeadCell[] = [
    {
      id: TableOrderByParams.TITLE,
      label: intl.formatMessage(tableHeaders.name),
      sort: true,
      align: 'left',
      padding: 'none',
    },
    {
      id: 'LEVEL',
      label: intl.formatMessage(tableHeaders.level),
      sort: false,
    },
    {
      id: TableOrderByParams.TYPE,
      label: intl.formatMessage(tableHeaders.type),
      sort: true,
    },
    {
      id: TableOrderByParams.STATUS,
      label: intl.formatMessage(tableHeaders.status),
      sort: true,
    },
  ];

  if (LEVEL_POLICY_EDIT || LEVEL_POLICY_CHANGE_STATUS) {
    headerOptions.push(HEADER_ACTIONS_OPTION);
  }

  const handleEditDialogShow = useCallback((policyItem: IPolicyListItem) => {
    setSelectedRow(policyItem);
    setShowEditDialog(true);
  }, []);

  const handleEditDialogClose = useCallback(() => {
    setSelectedRow(null);
    setShowEditDialog(false);
  }, [setShowEditDialog]);

  const handleViewDialogShow = useCallback((policyItem: IPolicyListItem) => {
    setSelectedRow(policyItem);
    setShowViewDialog(true);
  }, []);

  const handleViewDialogClose = useCallback(() => {
    setSelectedRow(null);
    setShowViewDialog(false);
  }, [setShowViewDialog]);

  const handleChangeStatusDialogShow = useCallback(
    (policyItem: IPolicyListItem) => {
      setSelectedRow(policyItem);
      setShowChangeStatusDialog(true);
    },
    [setShowChangeStatusDialog],
  );

  const handleChangeStatusDialogClose = useCallback(() => {
    setSelectedRow(null);
    setShowChangeStatusDialog(false);
  }, [setShowChangeStatusDialog]);

  const handleChangeStatusPolicyItem = useCallback(() => {
    if (selectedRow) {
      changeStatusItem(selectedRow.id, !selectedRow.active, handleChangeStatusDialogClose);
    }
  }, [changeStatusItem, selectedRow, handleChangeStatusDialogClose]);

  const tableRows: ITableRow[] = policiesList.map(policyValue => {
    const itemId = policyValue.id;
    const active = policyValue.active;
    const pLevel = getAppPermissionLevelByGroup(policyValue.permissionGroup);
    const pLevelMsg = pLevel ? getAppPermissionLevelLabel(pLevel) : null;
    let btnEdit: JSX.Element | null = null;
    let btnChangeStatus: JSX.Element | null = null;

    const bodyCells: IBodyCell[] = [
      {
        label: policyValue.title,
        className: 'body-cell',
        width: '25%',
        cellComponent: (
          <Typography
            variant="h5"
            style={{ cursor: 'pointer' }}
            onClick={() => handleViewDialogShow(policyValue)}
          >
            {policyValue.title}
          </Typography>
        ),
      },
      {
        label: 'level',
        className: 'body-cell',
        width: '25%',
        cellComponent: <Typography>{pLevelMsg ? intl.formatMessage(pLevelMsg) : ''}</Typography>,
      },
      {
        label: policyValue.permissionGroup,
        className: 'body-cell',
        width: '25%',
        cellComponent: (
          <Typography>{PermissionsGroupsLabels[policyValue.permissionGroup]}</Typography>
        ),
      },
      {
        label: '',
        className: 'body-cell',
        width: '25%',
        cellComponent: (
          <Typography variant="body1" color={active ? 'primary' : 'textSecondary'}>
            {intl.formatMessage(
              active ? commonMessages.activeStatus : commonMessages.inactiveStatus,
            )}
          </Typography>
        ),
      },
    ];

    if (LEVEL_POLICY_EDIT) {
      btnEdit = (
        <ActionItemEdit
          disabled={!active || !policyValue.changeable}
          onClick={() => handleEditDialogShow(policyValue)}
        />
      );
    }

    if (LEVEL_POLICY_CHANGE_STATUS) {
      btnChangeStatus = (
        <ActionItemChangeStatus
          active={policyValue.active}
          onClick={() => handleChangeStatusDialogShow(policyValue)}
        />
      );
    }

    if (btnEdit || btnChangeStatus) {
      bodyCells.push({
        label: 'actions',
        className: 'body-actions-cell',
        cellComponent: (
          <ActionsMenu horizontal tableActionsMode>
            {btnEdit}
            {btnChangeStatus}
          </ActionsMenu>
        ),
      });
    }

    return {
      id: itemId,
      cells: bodyCells,
    };
  });

  if (LEVEL_POLICY_CREATE) {
    btnCreate = (
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon className={classes.icon} />}
        onClick={() => setShowEditDialog(true)}
      >
        {intl.formatMessage(commonMessages.newBtn)}
      </Button>
    );
  }

  return (
    <>
      <Table
        addButton={btnCreate}
        filters={tableFilterSettings}
        suppressSmartBehavior
        suppressFiltersMobilePadding
        showPerPageSelect
        headerOptions={headerOptions}
        isLoading={isPoliciesListLoading}
        rows={tableRows}
        totalRows={policiesListMeta.total}
        onChangeParams={handleChangeTableProps}
        tableParams={tableParams}
      />
      {showChangeStatusDialog && (
        <ChangeStatusModal
          itemTitle={selectedRow?.title || ''}
          isOpen
          onClose={handleChangeStatusDialogClose}
          onSubmit={handleChangeStatusPolicyItem}
          isActive={!!selectedRow?.active}
          isLoading={isChangePolicyStatusLoading}
        />
      )}
      {showEditDialog && (
        <EditPolicyModal
          selectedRow={selectedRow}
          onSubmit={data => {
            if (selectedRow) {
              editItem(selectedRow.id, filterPermissionSet(data), handleEditDialogClose);
            } else {
              createItem(filterPermissionSet(data), handleEditDialogClose);
            }
          }}
          onClose={handleEditDialogClose}
          isLoading={isEditPolicyLoading}
        />
      )}
      {showViewDialog && selectedRow && (
        <ViewPolicyModal selectedRow={selectedRow} onClose={handleViewDialogClose} />
      )}
    </>
  );
};

export default PoliciesTable;
